/**
 * Is this value safe to use as ONE filesystem path segment?
 *
 * Shared by the renderer and the main process (`electron/` value-imports from
 * `src/types/` elsewhere, e.g. `autoUpdatesIpc.ts`) so there is one definition
 * rather than a copy per boundary that can drift.
 *
 * Why it exists: `path.join` **collapses `..`**. A `connectionId` of `'..'`
 * turns `join(metainfoRoot, connectionId, `${database}.json`)` into a write
 * one directory up — which for MetaInfo means `{userData}/<database>.json`,
 * i.e. an attacker-chosen file next to `connections.json`, or `connections.json`
 * itself. Any value that reaches a path segment and did not come from this
 * process must pass through here first.
 *
 * Modelled on `isValidWorkspaceFolderName` in `aiTerminalSettings.ts`, which
 * guards the same class of problem for AI workspace folder names.
 */
export function isSafePathSegment(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  if (value.length === 0) return false;
  // Separators would make this more than one segment.
  if (value.includes('/') || value.includes('\\')) return false;
  // `..` traverses; `.` is the current directory. Rejecting `..` anywhere
  // (not just as the whole segment) matches `isValidWorkspaceFolderName` and
  // costs nothing real — no database or connection id needs a double dot.
  if (value.includes('..')) return false;
  if (value === '.') return false;
  // A NUL truncates the path in some syscalls.
  if (value.includes('\0')) return false;
  return true;
}
